import { BaseDialog, IBaseDialogProps } from '@enter/core';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { ErrorDialog } from './error-dialog';
import { IErrorPayload, parseError } from './util';

export interface IDialogProps {
    props: Omit<IBaseDialogProps, 'open'>;
    Component: React.ReactNode;
}

export interface IDialogContextProps {
    showDialog: (props: IDialogProps) => void;
    hideDialog: () => void;
    showError: (err: Error) => void;
    hideError: () => void;
}

export const DialogContext = React.createContext<IDialogContextProps>({
    showDialog: () => undefined,
    hideDialog: () => undefined,
    showError: () => undefined,
    hideError: () => undefined,
});

const ignoredErrorNames = ['AbortError', 'TypeError'];
const ignoredErrorCodes = ['err.application_access_token_has_expired'];

export const DialogProvider: React.FC<{ children?: React.ReactNode }> = ({ children }): React.ReactElement => {
    const [dialogProps, setDialogProps] = useState<IDialogProps>();
    const [isOpen, setOpen] = useState(false);
    const [error, setError] = useState<Error>();
    const [payload, setPayload] = useState<IErrorPayload>();

    useEffect(() => {
        (async () => {
            if (error) {
                if (!_.includes(ignoredErrorNames, error.name)) {
                    const handled = await parseError(error);
                    if (!_.includes(ignoredErrorCodes, handled.payload.code)) {
                        setPayload(handled.payload);
                    }
                } else {
                    // tslint:disable-next-line:no-console
                    console.warn('Ignored error:', error);
                }
            }
        })();
    }, [error]);

    const showDialog = (props: IDialogProps): void => {
        setDialogProps(props);
        setOpen(true);
    };

    const hideDialog = (): void => {
        setOpen(false);
    };

    const showError = (err: Error): void => {
        setError(err);
    };

    const hideError = (): void => {
        setError(undefined);
        setPayload(undefined);
    };

    return (
        <DialogContext.Provider value={{ showDialog, hideDialog, showError, hideError }}>
            {children}
            {dialogProps && (
                <BaseDialog {...dialogProps.props} open={isOpen}>
                    {dialogProps && dialogProps.Component}
                </BaseDialog>
            )}
            {payload && (
                <BaseDialog
                    disableBackdropClick={false}
                    disableEscapeKeyDown={false}
                    renderTitle="A system exception has occurred"
                    onClose={hideError}
                    open={true}>
                    <ErrorDialog payload={payload} onClose={hideError} />
                </BaseDialog>
            )}
        </DialogContext.Provider>
    );
};
